import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { usePopover } from '@hooks/popover'
import { formatPrice } from '@utils/strings'
import { VenueLayoutSection, VenueLayoutTable } from 'types/model/venue-layout'
import { Venue } from 'types/model'
import { Popover } from '@elements/popover'
import { useCallback } from 'react'
import { UserCircleIcon } from '@heroicons/react/24/outline'
import { GuestCount } from '@modules/inventory/views/guest-count'
import { VenueLayoutTableSize } from 'shared/src/constants/venueLayoutTable'
import { resolveLegacyTableMarkerSizePx } from 'shared/src/utils/floorplanV2Canvas'

export function VenueMapTable({
  venue,
  table,
  soldOut = false,
  section,
  selectedSection,
  onSectionSelect,
  sectionMinPrice,
  sectionMaxGuests,
}: {
  venue: Venue
  table: VenueLayoutTable
  soldOut?: boolean
  section?: VenueLayoutSection
  selectedSection?: VenueLayoutSection
  onSectionSelect?: (sel?: VenueLayoutSection) => void
  sectionMinPrice?: number
  sectionMaxGuests?: number
}) {
  const { t } = useTranslation()
  const { setAnchorElement, ...popover } = usePopover({
    placement: 'top',
    flip: false,
    arrow: true,
    closeable: false,
    openOnAnchorHover: true,
    closeOnAnchorHoverExit: true,
  })

  const selectSection = useCallback(() => {
    if (soldOut) return
    onSectionSelect?.(section)
  }, [onSectionSelect, section, soldOut])

  const noAssociatedInventory = sectionMinPrice === undefined
  if (!section || noAssociatedInventory) return null
  const markerSize = resolveLegacyTableMarkerSizePx(
    table.size as VenueLayoutTableSize
  )
  const markerSizeScaled = Math.max(1, Math.round(markerSize * 0.5))
  const markerBorderRadius = table.type === 'ROUND' ? 9999 : 4
  return (
    <>
      <button
        type="button"
        ref={setAnchorElement}
        onClick={selectSection}
        style={{
          left: `${table.xPos}%`,
          top: `${table.yPos}%`,
          width: `${markerSizeScaled}px`,
          height: `${markerSizeScaled}px`,
          borderRadius: `${markerBorderRadius}px`,
        }}
        className={classNames(
          'peer p-0 m-0 absolute border-card border-2 transition ease-out duration-200 !origin-center z-0',
          {
            'bg-green-400 hover:scale-110 group-hover:!bg-primary-800':
              !soldOut,
            'bg-red-600 !cursor-not-allowed': soldOut,
            'bg-primary-500': section?.id === (selectedSection?.id ?? null),
            'rounded-full': table.type === 'ROUND',
          }
        )}
      >
        <div
          style={{ borderRadius: `${markerBorderRadius}px` }}
          className={classNames(
            'absolute inset-0 inline-flex h-full w-full animate-blink !origin-center border-card border-2 z-[-1]',
            {
              'bg-green-400  group-hover:!bg-primary-800': !soldOut,
              'bg-red-600 !cursor-not-allowed': soldOut,
              'bg-primary-500': section?.id === (selectedSection?.id ?? null),
              'rounded-full': table.type === 'ROUND',
            }
          )}
        ></div>
      </button>
      <Popover {...popover.props}>
        <div className="flex flex-col items-center justify-center py-5 space-y-4 px-7">
          <div className="flex flex-col items-center space-y-1.5">
            <span className="font-bold !text-md">{section.name}</span>
            {sectionMaxGuests && <GuestCount count={sectionMaxGuests} />}
          </div>
          {soldOut && (
            <span className="!text-base sm:!text-md text-error">
              {t('buyer.inventory.sold_out')}
            </span>
          )}{' '}
          {!soldOut && (
            <span className="!text-base sm:!text-md text-success">
              {formatPrice(sectionMinPrice, {
                currency: venue.currencyCode,
                languageCode: venue.languageCode,
              })}
            </span>
          )}
        </div>
      </Popover>
    </>
  )
}
